import { Fragment, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";

import ActionButton from "../formInputs/buttons/ActionButton";
import DcsdDialog from "../modals/DcsdDialog";

import "../../styles/BreadCrumb.scss";

const BreadCrumb = ({ isTouched, labelObj, setSelectedLink }) => {
    const [open, setOpen] = useState("false");
    const [clickedPath, setClickedPath] = useState("");

    const navigate = useNavigate();
    const location = useLocation();
    const { pathname } = location;

    const capitalize = (words) => {
        if (words === "sles") return "SLEs";
        if (words === "summary") return "Report Card Summary";
        if (words === "share") return "Manage Shares";
        if (words === "differentiation") return "Manage Differentiation";
        if (words === "overview") return "Report Card Overview";

        return words
            .split(/[\s-]+/)
            .map((word) => word[0].toUpperCase() + word.substring(1))
            .join(" ");
    };

    const crumbsFromUrl = useMemo(() => {
        if (!pathname || pathname === "/") return [{ label: "Home", path: "/home" }];

        const segments = pathname.split("/").filter(Boolean);

        let runningPath = "";
        const crumbs = [];

        let previousSeg = "";
        segments.forEach((seg) => {
            runningPath += `/${seg}`;

            // skip numeric IDs always
            if (!isNaN(Number(seg))) {
                previousSeg = seg;
                return;
            }

            if (seg.toLowerCase() === "location" && previousSeg.toLowerCase() === "overview") {
                previousSeg = seg;
                return;
            }

            const rawLabel = seg.toLowerCase();
            const label = rawLabel === "home" ? "Home" : capitalize(rawLabel);

            crumbs.push({ label, path: runningPath });
            previousSeg = seg;
        });

        // ensure Home always exists first when under /home
        if (segments[0] === "home" && (crumbs.length === 0 || crumbs[0].label !== "Home")) {
            crumbs.unshift({ label: "Home", path: "/home" });
        }

        return crumbs.length ? crumbs : [{ label: "Home", path: "/home" }];
    }, [pathname]);

    // Use URL crumbs when setSelectedLink exists (teacher view), else use incoming labelObj
    const crumbs = setSelectedLink ? crumbsFromUrl : labelObj;

    const handleOnClick = (path) => {
        if (path.toUpperCase() !== pathname.toUpperCase()) {
            if (isTouched) {
                setOpen("navigate");
                setClickedPath(path);
            } else {
                navigate(path);
            }
        }
    };

    return (
        <div className="breadCrumb">
            <DcsdDialog
                actions={
                    <div className="breadCrumb-btn-group">
                        <ActionButton
                            className="action-button-cancel"
                            label="Cancel"
                            onClick={() => setOpen("false")}
                        />
                        <ActionButton
                            className="action-button-reg"
                            label="Continue"
                            onClick={() => navigate(clickedPath)}
                        />
                    </div>
                }
                ariaLabel="Navigate Away Dialog"
                hasCloseX={false}
                id="navigate"
                open={open}
                title={<span style={{ color: "#CB321A" }}>Warning &ndash; Navigate Away</span>}
            >
                <div>You are about to navigate away from this page. Any unsaved work will be lost.</div>
            </DcsdDialog>
            {crumbs.map((obj, index) => {
                return (
                    <Fragment key={`${obj.path}-${obj.label}`}>
                        <button className="breadCrumb-btn" onClick={() => handleOnClick(obj.path)}>
                            {obj.label}
                        </button>
                        {index !== crumbs.length - 1 && <div className="breadCrumb-arrow">{">"}</div>}
                    </Fragment>
                );
            })}
        </div>
    );
};

BreadCrumb.propTypes = {
    isTouched: PropTypes.bool,
    labelObj: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            path: PropTypes.string.isRequired
        })
    ).isRequired,
    setSelectedLink: PropTypes.func
};

BreadCrumb.defaultProps = {
    isTouched: false,
    labelObj: [
        {
            label: "Home",
            path: "/home"
        }
    ],
    setSelectedLink: null
};

export default BreadCrumb;
